import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuth } from '../auth/AuthContext'
import { AgreementRow } from '../components/AgreementRow'
import { Button } from '../components/Button'
import { EmptyState } from '../components/EmptyState'
import { ReviewAcceptModal, type ReviewAcceptDocument } from '../components/ReviewAcceptModal'
import { SkeletonList } from '../components/Skeleton'
import { DocumentIcon } from '../components/icons'
import { getDb } from '../lib/db'
import { deriveAgreementStatus, recordAcceptance } from '../lib/download-flow'
import type { AcceptanceWithVersion, AgreementWithVersion } from '../lib/types'

interface ModalState {
  document: ReviewAcceptDocument
  mode: 'accept' | 'view'
  acceptedAt: string | null
  documentVersionId: string
}

/**
 * Agreements: the 8 legal documents at their CURRENT versions, with the
 * acceptance gate. "Action required" opens the Review & Accept modal
 * (summary bullets + full text + scroll-to-bottom + "Accept & Claim"); the
 * click records an immutable acceptance (user × version × server time ×
 * ip × user agent) and flips the chip.
 */
export function AgreementsPage() {
  const { user } = useAuth()
  const [agreements, setAgreements] = useState<AgreementWithVersion[]>([])
  const [acceptances, setAcceptances] = useState<AcceptanceWithVersion[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [modal, setModal] = useState<ModalState | null>(null)
  const [accepting, setAccepting] = useState(false)
  const [modalError, setModalError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [docs, accepts] = await Promise.all([
        getDb().listCurrentAgreements(),
        user ? getDb().listMyAcceptances(user.id) : Promise.resolve([]),
      ])
      setAgreements(docs)
      setAcceptances(accepts)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load agreements')
    } finally {
      setLoading(false)
    }
  }, [user])

  useEffect(() => {
    void load()
  }, [load])

  const acceptancesByDocument = useMemo(() => {
    const map = new Map<string, AcceptanceWithVersion[]>()
    for (const row of acceptances) {
      const docId = row.document_versions?.document_id
      if (!docId) continue
      const list = map.get(docId) ?? []
      list.push(row)
      map.set(docId, list)
    }
    return map
  }, [acceptances])

  const openAccept = (agreement: AgreementWithVersion, currentId: string) => {
    const current = agreement.document_versions[0]
    setModalError(null)
    setModal({
      document: {
        title: agreement.title,
        version: current?.version ?? '—',
        summary: current?.summary ?? null,
        rendered_text: current?.rendered_text ?? null,
      },
      mode: 'accept',
      acceptedAt: null,
      documentVersionId: currentId,
    })
  }

  const openAccepted = (agreement: AgreementWithVersion, accepted: AcceptanceWithVersion) => {
    const version = accepted.document_versions
    setModalError(null)
    setModal({
      document: {
        title: agreement.title,
        version: version?.version ?? '—',
        summary: null,
        rendered_text: version?.rendered_text ?? null,
      },
      mode: 'view',
      acceptedAt: accepted.accepted_at,
      documentVersionId: accepted.document_version_id,
    })
  }

  const handleAccept = async () => {
    if (!modal || !user) return
    setAccepting(true)
    setModalError(null)
    try {
      await recordAcceptance({ userId: user.id, documentVersionId: modal.documentVersionId })
      setModal(null)
      await load()
    } catch (err) {
      setModalError(err instanceof Error ? err.message : 'Failed to record your acceptance')
    } finally {
      setAccepting(false)
    }
  }

  return (
    <div className="flex flex-col gap-8">
      <header className="flex flex-col gap-3">
        <h1 className="font-display text-display-l text-ink">Agreements</h1>
        <p className="max-w-prose text-body-l text-ink-muted">
          The legal documents that govern your use of nMach services and products. Accept the current
          versions to unlock downloads and purchases.
        </p>
      </header>

      {loading && <SkeletonList rows={4} label="Loading agreements" />}

      {!loading && error && (
        <div className="flex flex-col items-start gap-4 rounded-2xl border border-terracotta-deep/30 bg-terracotta-deep/5 p-6">
          <p className="text-body text-terracotta-deep">{error}</p>
          <Button variant="secondary" onClick={() => void load()}>
            Try again
          </Button>
        </div>
      )}

      {!loading && !error && agreements.length === 0 && (
        <EmptyState
          icon={<DocumentIcon />}
          title="No agreements yet"
          description="Legal documents will appear here once they are published. You’ll be asked to accept the relevant ones at download time."
        />
      )}

      {!loading && !error && agreements.length > 0 && (
        <ul className="flex flex-col gap-3">
          {agreements.map((agreement) => {
            const current = agreement.document_versions[0]
            const rows = acceptancesByDocument.get(agreement.id) ?? []
            const status = current
              ? deriveAgreementStatus({
                  currentVersionId: current.id,
                  acceptances: rows.map((r) => ({
                    document_version_id: r.document_version_id,
                    accepted_at: r.accepted_at,
                  })),
                })
              : null
            const acceptedRow = rows.find((r) => r.document_version_id === current?.id) ?? rows[0]
            return (
              <AgreementRow
                key={agreement.id}
                agreement={agreement}
                status={status}
                acceptedRow={acceptedRow ?? null}
                onReview={() => openAccept(agreement, current.id)}
                onViewAccepted={(row) => openAccepted(agreement, row)}
              />
            )
          })}
        </ul>
      )}

      <p className="text-small text-ink-faint">
        Acceptances are recorded immutably per document version — user, version, server timestamp,
        IP address and user agent. When a document is updated, its new version must be accepted
        again before downloads resume.
      </p>

      <ReviewAcceptModal
        open={modal !== null}
        document={modal?.document ?? null}
        mode={modal?.mode ?? 'view'}
        acceptedAt={modal?.acceptedAt ?? null}
        accepting={accepting}
        error={modalError}
        onAccept={() => void handleAccept()}
        onClose={() => setModal(null)}
      />
    </div>
  )
}
